import React from 'react';
import { MdOutlineClose as _MdOutlineClose } from 'react-icons/md';
import { IoCheckmarkDone as _IoCheckmarkDone } from 'react-icons/io5';

import style from './styles.module.css';

const MdOutlineClose = _MdOutlineClose as React.ElementType;
const IoCheckmarkDone = _IoCheckmarkDone as React.ElementType;

export interface InputProps
  extends Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    | 'onChange'
    | 'onKeyPress'
    | 'type'
    | 'value'
    | 'name'
    | 'id'
    | 'disabled'
    | 'maxLength'
    | 'placeholder'
    | 'required'
    | 'onBlur'
    | 'onFocus'
    | 'pattern'
    | 'prefix'
    | 'readOnly'
    | 'style'
    | 'className'
  > {
  label?: string;
  error?: boolean;
  errorMessage?: string;
  success?: boolean;
  successMessage?: string;
  leftIcon?: React.ReactNode;
  status?: 'success' | 'error';
}

export const Input: React.FC<InputProps> = React.forwardRef<
  HTMLInputElement,
  InputProps
>(({ label, id = Math.random()
    .toString(36)
    .substring(7), error, errorMessage, className, leftIcon, status, success, successMessage, ...inputProps }, ref) => {
  const hasError = Boolean(error || status === 'error');
  const hasSuccess = (status === 'success' || success) && !hasError;
  const isRequired = Boolean(inputProps.required);

  const containerClassNames = [
    style.inputContainer,
    className,
    hasError ? style.inputError : '',
    hasSuccess ? style.inputSuccess : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputWrapperClassNames = [
    style.inputWrapper,
    leftIcon ? style.withLeftIcon : '',
    hasError || hasSuccess ? style.withStatusIcon : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={id} className={style.label}>
          <span id={id}>
            {label}
            {isRequired && (
              <span aria-hidden="true" className={style.requiredIndicator}>
                *
              </span>
            )}
          </span>
        </label>
      )}
      <div className={inputWrapperClassNames}>
        {leftIcon && <span className={style.leftIcon}>{leftIcon}</span>}
        <input id={id} className={style.input} ref={ref} {...inputProps} />
        {hasSuccess && (
          <IoCheckmarkDone
            aria-hidden="true"
            className={`${style.statusIcon} ${style.successIcon}`}
            size={20}
          />
        )}
        {hasError && (
          <MdOutlineClose
            aria-hidden="true"
            className={`${style.statusIcon} ${style.errorIcon}`}
            size={20}
          />
        )}
      </div>
      {error && <span className={style.errorMessage}>{errorMessage}</span>}
      {success && <div className={style.successMessage}>{successMessage}</div>}
    </div>
  );
});
